import { Button, Heading, Icon, Loader, Tooltip } from '@zoom/components';
import classnames from 'classnames';
import { addDays, differenceInDays, format, isWithinInterval, subDays } from 'date-fns';
import { toDate, utcToZonedTime } from 'date-fns-tz';
import { clone } from 'ramda';
import React, { useEffect, useMemo, useState } from 'react';
import { Form as FinalForm } from 'react-final-form';
import { useTranslation } from 'react-i18next';

import { AdherenceHistoryBottomPanelHeader } from './AdherenceHistoryBottomPanelHeader';
import { BarChart } from './BarChart';
import { BarChartLegend } from './BarChartLegend';
import { BarChartYAxisLabels } from './BarChartYAxisLabels';
import { GanttChart } from './GanttChart';
import { GanttChartYAxisLabels } from './GanttChartYAxisLabels';
import { TRANSITION_TIME_DELAY } from './config';
import { AdherenceHistoryBottomPanelProps, ChartTabState } from './types';
import { useAuth } from '../../app/AuthorizationProvider';
import { DatePickerValueType } from '../../components/DatePicker/DatePicker';
import { BottomPanel, BottomPanelBody } from '../../components/layout/BottomPanel';
import { Separator } from '../../components/layout/Separator';
import {
  AdherenceHistoryBarChart,
  useGetAdherenceHistoryBarChartDataQuery,
  useGetAdherenceHistoryGanttChartDataQuery,
  useGetScheduleElementsQuery,
} from '../../graphql/components';
import { getTimeZone, localDateISOFormat } from '../../utils/date';
import { convertFieldsEndOfDateRange } from '../../utils/dateRange';
import { DaySwitcherBar } from '../Scheduling/SchedulingComposer/DaySwitcherBar';
import { computeYAxisTicks } from '../Scheduling/utils/mapChartData';

export const calculateAgentDataByDate = (barChartData: AdherenceHistoryBarChart, day: Date) => {
  const time = day.getTime();

  const pickedDateIndex = barChartData.adherence.findIndex(([timestamp]) => timestamp === time);
  const adherence =
    barChartData.adherence[pickedDateIndex] && barChartData.adherence[pickedDateIndex][1] !== null
      ? String(barChartData.adherence[pickedDateIndex][1])
      : '0';
  const conformance =
    barChartData.conformance[pickedDateIndex] && barChartData.conformance[pickedDateIndex][1] !== null
      ? String(barChartData.conformance[pickedDateIndex][1])
      : '0';
  const dayData = barChartData.dataPerDay[time] ?? {};

  return {
    inAdherencePercentage: adherence,
    conformancePercentage: conformance,
    earlyLeavesCount: dayData.earlyLeaves ? 1 : 0,
    lateArrivalsCount: dayData.lateArrivals ? 1 : 0,
    outOfAdhEventsCount: dayData.outOfAdhEventsCount,
    outOfAdhTimeTotal: dayData.outOfAdhTimeTotal ?? 0,
    scheduledTime: dayData.scheduledTime ?? 0,
    scheduledWorkingTime: dayData.scheduledWorkingTime ?? 0,
  };
};

export const AdherenceHistoryBottomPanel: React.FC<AdherenceHistoryBottomPanelProps> = ({
  adherenceHistoryItem,
  endDateTime,
  lastDataImportDate,
  onClose,
  startDateTime,
  teams,
}) => {
  const { locale, localeObj } = useAuth();

  const localStartDateTime = format(startDateTime, localDateISOFormat, { locale: localeObj });
  const localEndDateTime = format(endDateTime, localDateISOFormat, { locale: localeObj });
  const { timezone } = adherenceHistoryItem;
  const localUserTimezone = getTimeZone();

  const zonedStartDateTime = toDate(localStartDateTime, { timeZone: timezone });
  const zonedEndDateTime = toDate(localEndDateTime, { timeZone: timezone });

  const hideBarChartTab = differenceInDays(zonedEndDateTime, zonedStartDateTime) < 2;

  const [chartTabsState, setChartTabsState] = useState<ChartTabState>(
    hideBarChartTab ? 'rightExpandedLeftHidden' : 'leftExpanded'
  );
  const [isChartTabsStateTransitioning, setChartTabsStateTransitioning] = useState<boolean>(false);
  const [pickedDate, setPickedDate] = useState<Date>(startDateTime);

  const localPickedDateTime = format(pickedDate, localDateISOFormat, { locale: localeObj });
  const zonedPickedDate = toDate(localPickedDateTime, { timeZone: timezone });
  const pickedDateEndTime = addDays(pickedDate, 1);

  const { t } = useTranslation();

  const handleTabsStateChange = (newState: ChartTabState) => () => {
    setChartTabsState(newState);
  };

  const handleOpenBothTabsState = () => {
    setChartTabsState('bothOpen');
  };

  const updatePickedDate = (newDate: DatePickerValueType) => {
    if (newDate && new Date(newDate).getTime() !== pickedDate.getTime()) {
      setPickedDate(newDate);
    }
  };

  const handleSelectDate = (dateTimeStamp: number) => {
    if (dateTimeStamp) {
      const newDateLocalTime = utcToZonedTime(dateTimeStamp, timezone);

      updatePickedDate(newDateLocalTime);
      setChartTabsState('bothOpen');
    }
  };

  const handleNextDay = () => {
    updatePickedDate(addDays(pickedDate, 1));
  };

  const handlePreviousDay = () => {
    updatePickedDate(subDays(pickedDate, 1));
  };

  useEffect(() => {
    setChartTabsStateTransitioning(true);
  }, [chartTabsState]);

  useEffect(() => {
    const shouldHideBarChartTab = differenceInDays(endDateTime, startDateTime) < 2;

    if (
      (shouldHideBarChartTab && chartTabsState !== 'rightExpandedLeftHidden') ||
      (!shouldHideBarChartTab && chartTabsState === 'rightExpandedLeftHidden')
    ) {
      setChartTabsState(shouldHideBarChartTab ? 'rightExpandedLeftHidden' : 'leftExpanded');
    }
  }, [endDateTime, startDateTime, chartTabsState]);

  useEffect(() => {
    if (!isWithinInterval(pickedDate, { start: startDateTime, end: endDateTime })) {
      setPickedDate(startDateTime);
    }
  }, [endDateTime, pickedDate, startDateTime]);

  useEffect(() => {
    let timeOut: NodeJS.Timeout;

    if (isChartTabsStateTransitioning) {
      timeOut = setTimeout(() => {
        setChartTabsStateTransitioning(false);
      }, TRANSITION_TIME_DELAY);
    }

    return () => {
      clearTimeout(timeOut);
    };
  }, [isChartTabsStateTransitioning]);

  const {
    data: barChartData,
    loading: isBarChartDataLoading,
    refetch: refetchBarChartData,
  } = useGetAdherenceHistoryBarChartDataQuery({
    variables: {
      agentId: adherenceHistoryItem.agentId,
      startDateTime: startDateTime.toISOString(),
      endDateTime: endDateTime.toISOString(),
      timezone: localUserTimezone, // this should represent local timezone, not agent's timezone
    },
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    skip: chartTabsState === 'rightExpandedLeftHidden',
  });

  let agentData = adherenceHistoryItem;

  const dayData = useMemo(
    () =>
      barChartData?.adherenceHistoryBarChartData
        ? calculateAgentDataByDate(barChartData.adherenceHistoryBarChartData, zonedPickedDate)
        : {},
    [barChartData?.adherenceHistoryBarChartData, zonedPickedDate]
  );

  if ((chartTabsState === 'bothOpen' || chartTabsState === 'rightExpanded') && barChartData) {
    agentData = {
      ...agentData,
      ...dayData,
    };
  }

  const {
    data: ganttChartData,
    loading: isGanttChartDataLoading,
    refetch: refetchGanttChartData,
  } = useGetAdherenceHistoryGanttChartDataQuery({
    variables: {
      agentId: adherenceHistoryItem.agentId,
      endDateTime: pickedDateEndTime.toISOString(),
      startDateTime: pickedDate.toISOString(),
      timezone: localUserTimezone, // this should represent local timezone, not agent's timezone
    },
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    skip: chartTabsState === 'leftExpanded',
  });

  const { data: scheduleElementsData, loading: isScheduleElementsDataLoading } = useGetScheduleElementsQuery();

  // Last data import refresh
  useEffect(() => {
    // Skip on first component render
    if (lastDataImportDate === null) {
      return;
    }

    if (!isBarChartDataLoading && (chartTabsState === 'leftExpanded' || chartTabsState === 'bothOpen')) {
      try {
        refetchBarChartData({
          agentId: adherenceHistoryItem.agentId,
          startDateTime: startDateTime.toISOString(),
          endDateTime: endDateTime.toISOString(),
          timezone: localUserTimezone, // this should represent local timezone, not agent's timezone
        });
      } catch (error) {
        //
      }
    }

    if (!isGanttChartDataLoading && chartTabsState !== 'leftExpanded') {
      try {
        refetchGanttChartData({
          agentId: adherenceHistoryItem.agentId,
          endDateTime: pickedDateEndTime.toISOString(),
          startDateTime: pickedDate.toISOString(),
          timezone: localUserTimezone,
        });
      } catch (error) {
        //
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastDataImportDate]);

  // content of gantt chart should not be rendered whenever real activity data are not available
  const isGanttChartDataEmpty = !ganttChartData?.adherenceHistoryGanttChartData?.agentActivityItems.length;
  const showLoaderForBarChart = isBarChartDataLoading && ['leftExpanded', 'bothOpen'].includes(chartTabsState);
  const showLoaderForGanttChart =
    (isGanttChartDataLoading || isScheduleElementsDataLoading) && chartTabsState !== 'leftExpanded';

  const yAxisTicks = computeYAxisTicks(barChartData?.adherenceHistoryBarChartData?.maxChartValue ?? 100, 0, 3, 10);

  return (
    <BottomPanel>
      <AdherenceHistoryBottomPanelHeader
        agentData={agentData}
        isOneDaySelected={chartTabsState !== 'leftExpanded'}
        onClose={onClose}
        teams={teams}
      />
      <BottomPanelBody className="adherence-history__detail-view-container">
        {chartTabsState !== 'rightExpandedLeftHidden' && (
          <>
            <div
              className={classnames('adherence-history__bar-chart', {
                'adherence-history__bar-chart--collapsed': ['rightExpanded', 'rightExpandedLeftHidden'].includes(
                  chartTabsState
                ),
                'adherence-history__bar-chart--expanded': chartTabsState === 'leftExpanded',
                'adherence-history__bar-chart--open': chartTabsState === 'bothOpen',
              })}
              data-test-id="adherence-history-detail:bar-chart-container"
            >
              <div
                className={classnames('adherence-history__chart-loader', 'adherence-history__chart-loader--bar-chart', {
                  'adherence-history__chart-loader--active': showLoaderForBarChart,
                })}
                data-test-id="adherence-history-detail:bar-chart-loader"
              >
                {showLoaderForBarChart && <Loader size={40} color="#63ad1a" />}
              </div>
              {chartTabsState === 'rightExpanded' ? (
                <Tooltip message={t('scheduleAdherence.multiDayOverview')} placement="bottom">
                  <Button
                    aria-label={t('scheduleAdherence.multiDayOverview')}
                    className="adherence-history__open-chart"
                    data-test-id="adherence-history-detail:button-open-bar-chart"
                    kind="info"
                    onClick={handleOpenBothTabsState}
                  >
                    <Icon kind="bar_chart" size={24} />
                  </Button>
                </Tooltip>
              ) : (
                <>
                  <div className="adherence-history__chart-header">
                    <Heading kind="section">{t('scheduleAdherence.adherencePerDay')}</Heading>
                    <BarChartLegend
                      className={
                        isChartTabsStateTransitioning ? 'adherence-history__bar-chart__legend--hidden' : undefined
                      }
                    />
                  </div>
                  <div className="d-flex">
                    <BarChartYAxisLabels isHidden={!barChartData} locale={locale} ticks={yAxisTicks} />
                    <BarChart
                      data={clone(barChartData?.adherenceHistoryBarChartData)}
                      handleSelectDate={handleSelectDate}
                      isContainerExpanded={chartTabsState === 'leftExpanded'}
                      isContainerOpen={chartTabsState === 'bothOpen'}
                      maxDate={zonedEndDateTime.getTime()}
                      minDate={zonedStartDateTime.getTime()}
                      pickedDate={zonedPickedDate.getTime()}
                      timezone={timezone}
                      t={t}
                      yAxisTicks={yAxisTicks}
                    />
                  </div>
                </>
              )}
            </div>
            {chartTabsState === 'bothOpen' && (
              <Tooltip message={t('scheduleAdherence.fullWidth')} placement="bottom">
                <Button
                  aria-label={t('scheduleAdherence.fullWidth')}
                  data-test-id="adherence-history-detail:button-expand-bar-chart"
                  kind="icon-bg"
                  onClick={handleTabsStateChange('leftExpanded')}
                  size="sm"
                >
                  <Icon kind="chevron-right" size={16} color="#00a8e2" />
                </Button>
              </Tooltip>
            )}
            <Separator className="adherence-history__separator" showBorder />
          </>
        )}
        <div
          className={classnames('adherence-history__gantt-chart', {
            'adherence-history__gantt-chart--expanded': chartTabsState === 'rightExpanded',
            'adherence-history__gantt-chart--expanded-solo': chartTabsState === 'rightExpandedLeftHidden',
            'adherence-history__gantt-chart--open': chartTabsState === 'bothOpen',
          })}
          data-test-id="adherence-history-detail:gantt-chart-container"
        >
          <div
            className={classnames('adherence-history__chart-loader', 'adherence-history__chart-loader--gantt-chart', {
              'adherence-history__chart-loader--active': showLoaderForGanttChart,
            })}
            data-test-id="adherence-history-detail:gantt-chart-loader"
          >
            {showLoaderForGanttChart && <Loader size={40} color="#63ad1a" />}
          </div>
          <div className="adherence-history__chart-header">
            {chartTabsState === 'leftExpanded' ? (
              <Tooltip message={t('scheduleAdherence.dailyOverview')} placement="bottom">
                <Button
                  aria-label={t('scheduleAdherence.dailyOverview')}
                  className="adherence-history__open-chart"
                  data-test-id="adherence-history-detail:button-open-gantt-chart"
                  kind="info"
                  onClick={handleOpenBothTabsState}
                >
                  <Icon kind="gantt_chart" size={24} />
                </Button>
              </Tooltip>
            ) : (
              <>
                <div className="adherence-history__chart-header-inner">
                  {chartTabsState === 'bothOpen' && (
                    <Tooltip message={t('scheduleAdherence.fullWidth')} placement="bottom">
                      <Button
                        aria-label={t('scheduleAdherence.fullWidth')}
                        data-test-id="adherence-history-detail:button-expand-gantt-chart"
                        kind="icon-bg"
                        onClick={handleTabsStateChange('rightExpanded')}
                        size="sm"
                      >
                        <Icon kind="chevron-left" size={16} color="#00a8e2" />
                      </Button>
                    </Tooltip>
                  )}
                  <Heading kind="section">{t('scheduleAdherence.realityVsScheduled')}</Heading>
                </div>
                <FinalForm
                  onSubmit={() => {
                    /* */
                  }}
                  initialValues={{ date: pickedDate }}
                  render={() => (
                    <DaySwitcherBar
                      dataTestId="adherence-history-detail:input-date-picker"
                      datePickerFormatKey="date.formats.default"
                      endTime={convertFieldsEndOfDateRange(endDateTime)}
                      handleDateChange={updatePickedDate}
                      handleNextDay={handleNextDay}
                      handlePreviousDay={handlePreviousDay}
                      nextDayButtonDataTestId="adherence-history-detail:next-day-button"
                      previousDayButtonDataTestId="adherence-history-detail:previous-day-button"
                      pickedTime={pickedDate}
                      startTime={startDateTime}
                      t={t}
                    />
                  )}
                />
              </>
            )}
          </div>
          {chartTabsState !== 'leftExpanded' && (
            <div className="d-flex align-items-end">
              <GanttChartYAxisLabels
                isHidden={
                  ((isGanttChartDataLoading || isScheduleElementsDataLoading) && !ganttChartData) ||
                  isGanttChartDataEmpty
                }
                slug="adherence-history"
                t={t}
              />
              <GanttChart
                data={ganttChartData?.adherenceHistoryGanttChartData}
                scheduleElementsData={scheduleElementsData?.scheduleElements}
                pickedDate={zonedPickedDate.getTime()}
                isLoading={isGanttChartDataLoading || isScheduleElementsDataLoading}
                isDataEmpty={isGanttChartDataEmpty}
                timezone={timezone}
              />
            </div>
          )}
        </div>
      </BottomPanelBody>
    </BottomPanel>
  );
};
